export const VPN_API = (process.env.REACT_APP_VPN_API_URL || "/api/vpn").replace(/\/+$/, "");

export const CLIENT_ID_KEY = "client_id";
/** Session issued by exchanging the bot's one-time login link. */
export const TG_SESSION_KEY = "tg_session";

export type LinkKind = "subscription" | "user" | "auth" | "session" | "pairing";

/** What a link token is for. The server owns the key, so it decides. */
export const describeLink = (token: string) =>
  vpnApi<{ kind: LinkKind }>(`/api/link/${encodeURIComponent(token)}`);

/** Trades the bot's one-time login link for a durable session token. */
export const exchangeTgLink = (token: string) =>
  vpnApi<{ session: string; tg_id: string }>("/api/auth/tg", "POST", { token });

/** A code and a QR link that hand this account to another device. */
export type PairingView = {
  code: string;
  token: string;
  expires_in: number;
  url: string;
};

/** Whoever a pairing code or token belonged to, once it is spent. */
export type PairedIdentity = {
  id_type: "tg" | "web";
  identity: string;
  session?: string;
};

export const pairQrUrl = (token: string) =>
  `${VPN_API}/api/pair/qr/${encodeURIComponent(token)}.png`;

export type ServerProtocols = Record<string, string>;

export type GateTarget = {
  name: string;
  has_protocols: boolean;
  protocols: ServerProtocols;
  server_key: string;
  /** Routes measured working through this gate; null when not measured lately. */
  alive?: number | null;
};

export type ServerEntry = {
  key: string;
  name: string;
  status: string;
  description: string;
  has_protocols: boolean;
  protocols: ServerProtocols;
  is_gate: boolean;
  is_auto?: boolean;
  /** For an auto entry: routes to that exit still working, across all gates. */
  alive?: number | null;
  targets: Record<string, GateTarget>;
};

export type ConfigEntry = {
  id: number | string;
  server_key: string;
  server_name: string;
  created_at: string;
  vless_config?: string;
  sub_link?: string;
  sub_id?: string;
  /** Transport suffix, so two configs on one server are distinguishable. */
  protocol?: string;
  /** "xray_json" for an auto config: a whole client config, not a link. */
  kind?: string;
};

/** One hop from a gate to an exit behind it, dialled by the gate's own panel. */
export type StatusExit = {
  gate: string;
  /** True when a balancer offers several exits, so they need telling apart. */
  shared: boolean;
  index: number;
  online: boolean;
  delay_ms: number | null;
};

export type StatusEntry = {
  key: string;
  name: string;
  description: string;
  online: boolean;
  details: string;
  /** False for an exit reachable only through a gate: it has no panel to poll. */
  has_panel: boolean;
  exits: StatusExit[];
};

/** Exits the gateway keeps per country, counted by state. Never named. */
export type LegCountry = {
  code: string;
  name: string;
  /** The leg actually carrying this country's traffic. */
  primary: number;
  /** Wired up to take over the moment the primary stops answering. */
  fallback: number;
  /** Older feeds did not split the two; kept so one still renders. */
  ok: number;
  /** Healthy, but outside the best few the gateway keeps: a spare, not a fault. */
  ranked_out: number;
  slow: number;
  dead: number;
  pruned: number;
  /** Megabytes per second, despite what the pruner calls the field. */
  best_mbytes: number;
  /** No leg of its own, but routing through one lent by another country. */
  borrowed: boolean;
  borrowed_from: string[];
};

export type LegsView = {
  known: boolean;
  generated?: number;
  summary?: {
    primary?: number;
    fallback?: number;
    ok?: number;
    ranked_out?: number;
    slow?: number;
    dead?: number;
    pruned?: number;
  };
  /** How many exits per country the gateway keeps in rotation. */
  keep_per_country?: number | null;
  slow_threshold_mbps?: number | null;
  countries: LegCountry[];
};

export type SubscriptionView = {
  server_name: string;
  vless_config: string;
  sub_link: string;
  created_at: string;
  sub_url: string;
  sub_app: string;
};

export type CostItem = {
  label: string;
  amount: number;
  currency: string;
  period: "month" | "quarter";
  monthly_rub: number;
  metered: boolean;
  per_gb_rub?: number | null;
  used_gb?: number | null;
  traffic_rub?: number | null;
  live?: boolean;
  /** Set when the meter only started partway through the month. */
  since?: string | null;
};

/** Traffic actually recorded for a closed month. Flat fees are today's prices. */
export type CostsPrevious = {
  period: string;
  known: boolean;
  items: { label: string; used_gb: number; traffic_rub: number }[];
  metered_total_rub: number;
  total_rub: number;
};

export type CostsView = {
  period: string;
  previous?: CostsPrevious;
  currency: string;
  rates: { USD: number | null; EUR: number | null; as_of: string; source: string };
  items: CostItem[];
  fixed_total_rub: number;
  metered_total_rub: number;
  total_rub: number;
};

export type Challenge = { digest: string; difficulty: number };

export class VpnApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/**
 * True when the server could not parse the fingerprint at all, as opposed to
 * parsing it and deciding it looks like a bot. Only the former is worth
 * retrying: a fresh payload fixes a malformed one, but re-sending a valid
 * payload that scored badly just repeats the same verdict.
 */
export function isPayloadRejection(e: unknown): boolean {
  return e instanceof VpnApiError && e.status === 403 && /invalid payload/i.test(e.message);
}

export async function vpnApi<T>(
  path: string,
  method: "GET" | "POST" | "DELETE" = "GET",
  body?: unknown,
  headers: Record<string, string> = {},
): Promise<T> {
  const res = await fetch(`${VPN_API}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...headers },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const parsed = await res.json();
      if (parsed?.detail) detail = String(parsed.detail);
    } catch {}
    throw new VpnApiError(detail, res.status);
  }
  return res.json();
}

export const qrUrl = (subId: string) => `${VPN_API}/api/qr/${encodeURIComponent(subId)}.png`;

export const shortDate = (value: string) => (value || "").split(/[T ]/)[0];

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    area.remove();
    return ok;
  }
}
